import { useEffect, useState } from 'react';
import { useT } from '../i18n/index.js';

/**
 * Says when the device itself is offline.
 *
 * Distinct from the "connection lost" banner on the swipe screen, which is
 * about our event stream. This is about the network, and it belongs at the app
 * level: somebody offline on the landing screen should learn that from the app
 * rather than from a button that quietly does nothing.
 *
 * `navigator.onLine` is famously optimistic — true means "there is an
 * interface", not "the internet works". It is reliable in the direction that
 * matters here: false really does mean nothing will go through.
 */
export function OfflineBanner() {
  const t = useT();
  const [offline, setOffline] = useState(
    () => typeof navigator !== 'undefined' && !navigator.onLine,
  );

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!offline) return null;
  return (
    <p className="banner banner--warn banner--sticky" role="status">
      {t('error.offline')}
    </p>
  );
}
